/**
 * Formats a duration in seconds to a string in the format "m:ss"
 * @param seconds - The duration in seconds
 * @returns A formatted string in the format "m:ss"
 */
export const formatDuration = (seconds: number): string => {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Formats a phone number for display readability with country-aware formatting
 * @param phone - The phone number string (may include + prefix)
 * @returns A formatted phone number string for display
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // US/Canada (country code 1)
  if (digits.length === 11 && digits.startsWith('1')) {
    const area = digits.slice(1, 4);
    const exchange = digits.slice(4, 7);
    const number = digits.slice(7, 11);
    return `+1 (${area}) ${exchange}-${number}`;
  } else if (digits.length === 10 && /^[2-9]\d{2}[2-9]\d{2}\d{4}$/.test(digits)) {
    // US/Canada without country code
    const area = digits.slice(0, 3);
    const exchange = digits.slice(3, 6);
    const number = digits.slice(6, 10);
    return `(${area}) ${exchange}-${number}`;
  }

  // UK (country code 44)
  else if (digits.length >= 11 && digits.startsWith('44')) {
    const number = digits.substring(2);
    return `+44 ${number.substring(0, 2)} ${number.substring(2, 6)} ${number.substring(6)}`;
  }

  // Germany (country code 49)
  else if (digits.length >= 11 && digits.startsWith('49')) {
    const number = digits.substring(2);
    return `+49 ${number.substring(0, 3)} ${number.substring(3, 6)} ${number.substring(6)}`;
  }

  // France (country code 33)
  else if (digits.length === 12 && digits.startsWith('33')) {
    const number = digits.substring(2);
    return `+33 ${number.substring(0, 1)} ${number.substring(1, 3)} ${number.substring(3, 5)} ${number.substring(5, 7)} ${number.substring(7)}`;
  }

  // Australia (country code 61)
  else if (digits.length === 11 && digits.startsWith('61')) {
    const number = digits.substring(2);
    return `+61 ${number.substring(0, 1)} ${number.substring(1, 5)} ${number.substring(5)}`;
  }

  // India (country code 91)
  else if (digits.length === 12 && digits.startsWith('91')) {
    const number = digits.substring(2);
    return `+91 ${number.substring(0, 5)} ${number.substring(5)}`;
  }

  // Japan (country code 81)
  else if (digits.length >= 11 && digits.startsWith('81')) {
    const number = digits.substring(2);
    return `+81 ${number.substring(0, 2)} ${number.substring(2, 6)} ${number.substring(6)}`;
  }

  // Brazil (country code 55)
  else if (digits.length >= 12 && digits.startsWith('55')) {
    const number = digits.substring(2);
    return `+55 ${number.substring(0, 2)} ${number.substring(2, 7)} ${number.substring(7)}`;
  }

  // Generic international format for other countries
  else if (digits.length > 10) {
    const countryCode = digits.substring(0, digits.length >= 12 ? 2 : 1);
    const number = digits.substring(countryCode.length);
    const formatted = number.match(/.{1,4}/g)?.join(' ') || number;
    return `+${countryCode} ${formatted}`;
  }

  // Short numbers or fallback
  else if (digits.length >= 7) {
    return digits.replace(/(\d{3})(\d{3})(\d+)/, '$1-$2-$3');
  }

  return digits;
}; 